/** Whether the visitor has asked for less motion. */
function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Where a navigation leaves the viewport.
 *
 * There was no scrollBehavior at all, which is not the same as having a
 * default: vue-router's history mode then leaves the scroll position exactly
 * where it was, so following a link from halfway down the shop dropped you
 * halfway down the next page. On a long page — /premium, a profile, a blog
 * post — that reads as the page having failed to load rather than as a scroll
 * position, because the top of the content is nowhere in sight.
 *
 * Lives in its own module rather than inline in the router for the same
 * reason the guards next to it do: so it can be exercised without
 * constructing a router and pulling every view into the test.
 *
 * @param {object} to
 * @param {object} from
 * @param {{x: number, y: number}|null} savedPosition
 * @returns {object|undefined} A vue-router scroll position, or nothing to
 *   leave the page where it is.
 */
export function scrollBehavior(to, from, savedPosition) {
    // Back and forward restore where the reader actually was. Browsers do
    // this for real navigations and people expect it of an SPA too — checked
    // first, because it should win over both cases below.
    if (savedPosition) return savedPosition

    // An anchor is an explicit request for a position, so it beats the top of
    // the page. /premium's own scrollTo() ends up here.
    if (to.hash) {
        return {
            selector: to.hash,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        }
    }

    /*
     * Same page, different query — Stripe's `?subscribed=1` on return from
     * checkout, a `?ref=` from a bot link, a season picked on the stats
     * panel. Scrolling to the top for those would yank the reader away from
     * what they were reading in order to show them the same page again.
     *
     * Compared on `path`, which excludes the query: /profile/:id and
     * /profile/:id/stats are different paths and do scroll, because they
     * render different panels; /profile/:id?ref=command is the same path and
     * does not.
     */
    if (to.path === from.path) return undefined

    return { x: 0, y: 0 }
}

export default scrollBehavior
